import type { ReactNode } from 'react';
import type { RootState } from 'src/redux/store';

import { toast } from 'sonner';
import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import { useTranslate } from 'src/locales';
import { useAppSelector } from 'src/hook/hooks';
import { useWebSocket } from 'src/contexts/websocket-context';
import { useGetMediaUrlMutation } from 'src/pages/chat/slice/chatApi';
import { useFilterContactMutation } from 'src/pages/contacts/slice/contactsApi';

import { WhatsAppCallContext } from './whatsapp-call-context';
import { useInitiateWhatsAppCallMutation } from '../slice/whatsappCallApi';
import { getWhatsAppApiErrorMessage } from '../utils/whatsapp-enable-errors';
import {
  teardownPeer,
  attachRemoteAudio,
  applyRemoteSdpAnswer,
  localDescriptionWhenReady,
  createWhatsAppPeerConnection,
} from '../utils/webrtc-utils';

/** Outbound: cap ICE wait so initiate runs in a few seconds, not 15–30s. */
const OUTBOUND_ICE_MAX_MS = 4000;
const OUTBOUND_ICE_MIN_MS = 400;

import type {
  PeerEntry,
  WhatsAppCallId,
  WhatsAppCallSession,
  WhatsAppCallEndedPayload,
  WhatsAppCallAnswerPayload,
  WhatsAppCallFailedPayload,
  WhatsAppCallIncomingPayload,
  WhatsAppCallConnectedPayload,
} from '../types/whatsapp-call.types';

// ----------------------------------------------------------------------

function contactDisplayName(contact?: { firstName?: string; lastName?: string; phone?: string }) {
  if (!contact) return '';
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim();
  return name || contact.phone || '';
}

const RINGTONE_SRC = '/ringtone/call-ringtone.mp3';

// ----------------------------------------------------------------------

/**
 * WhatsAppCallProvider component.
 * Acts as the centralized state manager and signaling coordinator for WhatsApp VoIP Calling.
 *
 * Core Responsibilities:
 * 1. WebRTC Peer Connection Management: Spawns, tracks, and cleans up RTCPeerConnections mapped by callId.
 * 2. Signaling Event wiring: Subscribes to Socket.IO events (`whatsapp:call:incoming`, `whatsapp:call:answer`, etc.)
 *    and dispatches outbound signaling emits (`whatsapp:call:accept`, `whatsapp:call:reject`, etc.).
 * 3. Media Capture: Captures mic audio stream dynamically via `getUserMedia` respecting browser security/autoplay settings.
 * 4. UX & Assets: Plays ringtone sounds, handles volume/muting/holding states, and searches CRM contacts to resolve Caller ID name/avatars.
 *
 * Designed with a fallback to REST endpoints for non-WebSocket client environments.
 */
export function WhatsAppCallProvider({ children }: { children: ReactNode }) {
  const { t, currentLang } = useTranslate('translation');
  
  // Real-time signaling transporter utilizing the WebSocket connection namespace /{tenantId}
  const { subscribe, send, status } = useWebSocket();
  
  // RTK Mutation to start the REST API outbound call initiate transaction
  const [initiateCall] = useInitiateWhatsAppCallMutation();
  
  // CRM helper mutation to resolve contact names from phone numbers
  const [filterContact] = useFilterContactMutation();
  
  // Media server helper mutation to resolve public profile image links
  const [getMediaUrl] = useGetMediaUrlMutation();

  const getProfileUrl = useCallback(
    async (filePath: string | null) => {
      if (filePath) {
        try {
          const res = await getMediaUrl({ fileName: filePath }).unwrap();
          return res || '';
        } catch (err) {
          console.warn('[WA Call] Failed to get media URL for profile pic:', err);
        }
      }
      return '';
    },
    [getMediaUrl]
  );

  const resolveContactDetails = useCallback(
    async (phoneNumber?: string, contactId?: string) => {
      try {
        let cleanPhone = phoneNumber ? phoneNumber.replace(/\D/g, '') : '';
        if (cleanPhone.startsWith('0')) {
          cleanPhone = cleanPhone.slice(1);
        }
        if (cleanPhone.length >= 10) {
          cleanPhone = cleanPhone.slice(-9); // last 9 digits, matches saved numbers with any country code/format
        }

        // Filters are combined with AND on the API, so search by phone first and only
        // fall back to contact id (a non-CRM id would otherwise wipe out the phone match).
        const searches: Array<{ fieldname: string; search: string }> = [];
        if (cleanPhone.length >= 5) {
          searches.push({ fieldname: 'phone', search: cleanPhone });
        }
        if (contactId) {
          searches.push({ fieldname: 'id', search: contactId });
        }

        let contact: any = null;
        for (const filter of searches) {
          const res = await filterContact({
            filters: [filter],
            globalSearch: '',
            page: 1,
            limit: 1,
          }).unwrap();
          contact = res?.list?.[0];
          if (contact) break;
        }

        if (contact) {
          const profileUrl = contact.ProfileURL ? await getProfileUrl(contact.ProfileURL) : '';
          const name =
            [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim() ||
            contact.phone ||
            '';
          return {
            contactName: name,
            avatarUrl: profileUrl,
          };
        }
      } catch (err) {
        console.error('[WA Call] Error resolving contact details:', err);
      }
      return null;
    },
    [filterContact, getProfileUrl]
  );

  const { channelFeatureFlags = {}, channelFeatureFlagsInitialized } = useAppSelector(
    (state: RootState) => state.auth
  );
  const whatsAppCallAllowed =
    channelFeatureFlagsInitialized && channelFeatureFlags.WHATSAPP === true;

  // Active call sessions dictionary mapped by callId
  const [calls, setCalls] = useState<Record<string, WhatsAppCallSession>>({});
  // Busy indicator preventing concurrent outgoing call attempts
  const [isBusy, setIsBusy] = useState(false);

  // References keeping track of raw RTCPeerConnection instances to bypass React re-renders
  const peersRef = useRef<Map<WhatsAppCallId, PeerEntry>>(new Map());
  // Stores remote SDP offers received before the agent selects 'Answer'
  const pendingOffersRef = useRef<Map<WhatsAppCallId, string>>(new Map());
  // Stores remote SDP answers received before the local PeerConnection transition settles
  const pendingAnswersRef = useRef<Map<WhatsAppCallId, string>>(new Map());
  // Tracks active setRemoteDescription(answer) promises to avoid race conditions
  const applyingAnswerRef = useRef<Set<WhatsAppCallId>>(new Set());
  // Keeps track of ended call IDs to reject out-of-order stale socket signals
  const endedCallIdsRef = useRef<Set<WhatsAppCallId>>(new Set());
  // Tracks muted call IDs locally
  const mutedCallsRef = useRef<Set<WhatsAppCallId>>(new Set());
  // Tracks held call IDs locally
  const heldCallsRef = useRef<Set<WhatsAppCallId>>(new Set());
  // Warmup PeerConnection to pre-gather ICE candidates in local cache
  const iceWarmupPcRef = useRef<RTCPeerConnection | null>(null);

  // Ringtone handling using useRef to avoid DOM leaks and multi-instance collisions
  const ringtoneAudioRef = useRef<HTMLAudioElement | null>(null);

  const getRingtoneAudio = useCallback((): HTMLAudioElement => {
    if (!ringtoneAudioRef.current) {
      ringtoneAudioRef.current = document.getElementById(
        'wa-call-ringtone'
      ) as HTMLAudioElement | null;
    }
    if (!ringtoneAudioRef.current) {
      const audio = document.createElement('audio');
      audio.id = 'wa-call-ringtone';
      audio.preload = 'auto';
      audio.loop = true;
      document.body.appendChild(audio);
      ringtoneAudioRef.current = audio;
    }
    return ringtoneAudioRef.current;
  }, []);

  const stopRingtone = useCallback(() => {
    const audio =
      ringtoneAudioRef.current ||
      (document.getElementById('wa-call-ringtone') as HTMLAudioElement | null);
    if (!audio) return;

    audio.pause();
    audio.currentTime = 0;
    audio.loop = false;
    audio.removeAttribute('src');
    audio.load();
  }, []);

  const playRingtone = useCallback(() => {
    stopRingtone();
    const audio = getRingtoneAudio();
    audio.src = RINGTONE_SRC;
    audio.loop = true;
    audio.currentTime = 0;
    audio.play().catch((error) => {
      console.warn('[WA Call] Autoplay blocked for ringtone:', error);
      toast.error(t('WHATSAPP_CALL_INCOMING'), {
        description:
          t('WHATSAPP_CALL_AUTOPLAY_BLOCKED_HINT') ||
          'Click anywhere to enable incoming call audio alerts.',
        duration: 10000,
      });
    });
  }, [getRingtoneAudio, stopRingtone, t]);

  // Outbound ringback tone (synthesized, 400Hz+450Hz) played until the callee answers
  const ringbackRef = useRef<{ ctx: AudioContext; timer: ReturnType<typeof setInterval> } | null>(
    null
  );

  const stopRingback = useCallback(() => {
    const ringback = ringbackRef.current;
    if (!ringback) return;
    ringbackRef.current = null;
    clearInterval(ringback.timer);
    void ringback.ctx.close().catch(() => undefined);
  }, []);

  const startRingback = useCallback(() => {
    if (ringbackRef.current) return;
    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;

    try {
      const ctx = new AudioCtx();
      const gain = ctx.createGain();
      gain.gain.value = 0;
      gain.connect(ctx.destination);
      [400, 450].forEach((freq) => {
        const osc = ctx.createOscillator();
        osc.frequency.value = freq;
        osc.connect(gain);
        osc.start();
      });

      // Cadence: 0.4s on, 0.2s off, 0.4s on, 2s off (3s cycle)
      const scheduleCycle = () => {
        const now = ctx.currentTime;
        gain.gain.setValueAtTime(0.08, now);
        gain.gain.setValueAtTime(0, now + 0.4);
        gain.gain.setValueAtTime(0.08, now + 0.6);
        gain.gain.setValueAtTime(0, now + 1.0);
      };
      void ctx.resume().catch(() => undefined);
      scheduleCycle();
      const timer = setInterval(scheduleCycle, 3000);
      ringbackRef.current = { ctx, timer };
    } catch (error) {
      console.warn('[WA Call] Failed to start ringback tone:', error);
    }
  }, []);

  // Audio input device selection state
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [selectedMicrophoneId, setSelectedMicrophoneIdState] = useState<string>(
    () => localStorage.getItem('wa_selected_microphone_id') || ''
  );

  const setSelectedMicrophoneId = useCallback((deviceId: string) => {
    setSelectedMicrophoneIdState(deviceId);
    localStorage.setItem('wa_selected_microphone_id', deviceId);
    // console.log('[WA Call] Selected microphone device set to:', deviceId);
  }, []);

  const updateMicrophonesList = useCallback(async () => {
    try {
      if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
        console.warn('[WA Call] navigator.mediaDevices is not supported in this environment (e.g. non-secure context)');
        return;
      }
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter((device) => device.kind === 'audioinput');
      setMicrophones(audioInputs);

      // Auto-select first/default microphone if not configured
      if (audioInputs.length > 0 && !selectedMicrophoneId) {
        const defaultDevice = audioInputs.find((d) => d.deviceId === 'default') || audioInputs[0];
        setSelectedMicrophoneIdState(defaultDevice.deviceId);
      }
    } catch (err) {
      console.warn('[WA Call] Failed to list microphones:', err);
    }
  }, [selectedMicrophoneId]);

  useEffect(() => {
    void updateMicrophonesList();
    if (typeof navigator !== 'undefined' && navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener('devicechange', updateMicrophonesList);
    }
    return () => {
      if (typeof navigator !== 'undefined' && navigator.mediaDevices?.removeEventListener) {
        navigator.mediaDevices.removeEventListener('devicechange', updateMicrophonesList);
      }
    };
  }, [updateMicrophonesList]);

  // Safety net ringtone cleanup on unmount
  useEffect(
    () => () => {
      stopRingtone();
      stopRingback();
      if (ringtoneAudioRef.current) {
        ringtoneAudioRef.current.remove();
        ringtoneAudioRef.current = null;
      }
    },
    [stopRingtone, stopRingback]
  );

  const upsertCall = useCallback((callId: WhatsAppCallId, patch: Partial<WhatsAppCallSession>) => {
    setCalls((prev) => {
      const existing = prev[callId];
      const next: WhatsAppCallSession = {
        ...existing,
        callId,
        direction: existing?.direction ?? 'INBOUND',
        status: existing?.status ?? 'RINGING',
        ...patch,
      };
      return { ...prev, [callId]: next };
    });
  }, []);

  const callsRef = useRef(calls);
  callsRef.current = calls;

  const dismissCall = useCallback((callId: WhatsAppCallId) => {
    setCalls((prev) => {
      if (!prev[callId]) return prev;
      const next = { ...prev };
      delete next[callId];
      return next;
    });
  }, []);

  // Outbound: polls inbound audio stats to detect when the callee picks up
  const answerWatchersRef = useRef<Map<WhatsAppCallId, ReturnType<typeof setInterval>>>(new Map());

  const stopAnswerWatcher = useCallback((callId: WhatsAppCallId) => {
    const timer = answerWatchersRef.current.get(callId);
    if (timer) clearInterval(timer);
    answerWatchersRef.current.delete(callId);
  }, []);

  const markOutboundAnswered = useCallback(
    (callId: WhatsAppCallId) => {
      stopAnswerWatcher(callId);
      if (endedCallIdsRef.current.has(callId) || callsRef.current[callId]?.connectedAt) return;
      upsertCall(callId, { status: 'ACTIVE', connectedAt: Date.now() });
    },
    [stopAnswerWatcher, upsertCall]
  );

  const startAnswerWatcher = useCallback(
    (callId: WhatsAppCallId, pc: RTCPeerConnection) => {
      stopAnswerWatcher(callId);
      let lastEnergy = 0;

      // Meta establishes media before the callee picks up but sends no audio until then,
      // so the first real inbound audio energy means the call was answered.
      const timer = setInterval(async () => {
        if (pc.connectionState === 'closed' || endedCallIdsRef.current.has(callId)) {
          stopAnswerWatcher(callId);
          return;
        }
        try {
          const stats = await pc.getStats();
          let energy = 0;
          let level = 0;
          stats.forEach((report) => {
            if (report.type === 'inbound-rtp' && report.kind === 'audio') {
              energy = report.totalAudioEnergy ?? 0;
              level = report.audioLevel ?? 0;
            }
          });
          if (energy - lastEnergy > 1e-6 || level > 0.001) {
            markOutboundAnswered(callId);
          }
          lastEnergy = energy;
        } catch (error) {
          console.warn('[WA Call] Failed to read call stats:', error);
        }
      }, 500);

      answerWatchersRef.current.set(callId, timer);
    },
    [markOutboundAnswered, stopAnswerWatcher]
  );

  const teardownCallMedia = useCallback((callId: WhatsAppCallId) => {
    stopAnswerWatcher(callId);
    teardownPeer(callId, peersRef.current);
    pendingOffersRef.current.delete(callId);
    pendingAnswersRef.current.delete(callId);
    applyingAnswerRef.current.delete(callId);
    mutedCallsRef.current.delete(callId);
    heldCallsRef.current.delete(callId);
  }, [stopAnswerWatcher]);

  const markCallEnded = useCallback(
    (callId: WhatsAppCallId, patch: Partial<WhatsAppCallSession>) => {
      const existing = callsRef.current[callId];
      if (!existing && !callId.startsWith('outbound-')) return;

      if (!endedCallIdsRef.current.has(callId)) {
        endedCallIdsRef.current.add(callId);
        teardownCallMedia(callId);
      }

      // Directly close the dialog by removing the call from state immediately
      stopRingtone();
      dismissCall(callId);
      setIsBusy(false);
    },
    [teardownCallMedia, dismissCall, stopRingtone]
  );

  const tryApplyRemoteAnswer = useCallback(
    async (callId: WhatsAppCallId, sdpAnswer: string) => {
      if (!sdpAnswer || endedCallIdsRef.current.has(callId)) return;

      const entry = peersRef.current.get(callId);
      if (!entry) {
        pendingAnswersRef.current.set(callId, sdpAnswer);
        return;
      }

      if (applyingAnswerRef.current.has(callId)) return;
      applyingAnswerRef.current.add(callId);

      try {
        const result = await applyRemoteSdpAnswer(entry.pc, sdpAnswer);

        if (result === 'already_applied' || result === 'applied') {
          pendingAnswersRef.current.delete(callId);
          upsertCall(callId, { status: 'CONNECTING' });
          return;
        }

        console.warn(
          `[WA Call] Skipped duplicate or out-of-order SDP answer for ${callId} (signalingState=${entry.pc.signalingState})`
        );
      } catch (error) {
        if (entry.pc.signalingState === 'stable' && entry.pc.remoteDescription?.type === 'answer') {
          pendingAnswersRef.current.delete(callId);
          upsertCall(callId, { status: 'CONNECTING' });
          return;
        }

        console.error('Failed to apply SDP answer:', error);
        toast.error('Call connection failed.');
        markCallEnded(callId, { status: 'FAILED' });
      } finally {
        applyingAnswerRef.current.delete(callId);
      }
    },
    [markCallEnded, upsertCall]
  );

  const flushPendingAnswer = useCallback(
    (callId: WhatsAppCallId) => {
      const sdpAnswer = pendingAnswersRef.current.get(callId);
      if (!sdpAnswer) return;
      void tryApplyRemoteAnswer(callId, sdpAnswer);
    },
    [tryApplyRemoteAnswer]
  );

  const createPeerWithMic = useCallback(
    async (callId: WhatsAppCallId, direction: WhatsAppCallSession['direction']) => {
      const activeDeviceId = selectedMicrophoneId;
      const constraints: MediaStreamConstraints = {
        audio: activeDeviceId ? { deviceId: { exact: activeDeviceId } } : true,
      };
      // console.log('[WA Call] getUserMedia with constraints:', constraints);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      const pc = createWhatsAppPeerConnection();

      // Automatically sync state when connection physically establishes or fails
      pc.onconnectionstatechange = () => {
        // console.log(`[WA Call] pc.connectionState changed to:`, pc.connectionState);

        let activeCallId = callId;
        for (const [id, entry] of peersRef.current.entries()) {
          if (entry.pc === pc) {
            activeCallId = id;
            break;
          }
        }

        if (pc.connectionState === 'connected') {
          // Outbound: Meta connects media before the callee picks up, so wait for
          // the `whatsapp:call:connected` signal instead of starting the call here.
          if (direction === 'OUTBOUND') return;
          stopRingtone();
          upsertCall(activeCallId, { status: 'ACTIVE', connectedAt: Date.now() });
        } else if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
          markCallEnded(activeCallId, { status: 'FAILED' });
        }
      };

      stream.getTracks().forEach((track) => pc.addTrack(track, stream));
      attachRemoteAudio(pc, callId);
      peersRef.current.set(callId, { pc, stream });
      return { pc, stream };
    },
    [selectedMicrophoneId, upsertCall, markCallEnded, stopRingtone]
  );

  // Pre-gather ICE while the agent is online so the first outbound call is faster.
  useEffect(() => {
    if (status !== 'connected' || !whatsAppCallAllowed) {
      iceWarmupPcRef.current?.close();
      iceWarmupPcRef.current = null;
      return undefined;
    }

    const pc = createWhatsAppPeerConnection();
    iceWarmupPcRef.current = pc;
    pc.createDataChannel('wa-ice-warmup');
    void pc.createOffer().then((offer) => pc.setLocalDescription(offer));

    return () => {
      pc.close();
      if (iceWarmupPcRef.current === pc) {
        iceWarmupPcRef.current = null;
      }
    };
  }, [status, whatsAppCallAllowed]);

  const socketEmit = useCallback(
    (type: string, payload: Record<string, unknown>) => {
      send({ type, payload });
    },
    [send]
  );

  const acceptCall = useCallback(
    async (callId: WhatsAppCallId) => {
      stopRingtone();

      if (endedCallIdsRef.current.has(callId)) return;

      const sdpOffer = pendingOffersRef.current.get(callId);
      if (!sdpOffer) {
        toast.error('Call offer is missing. Cannot answer.');
        return;
      }

      try {
        upsertCall(callId, { status: 'CONNECTING' });

        const { pc } = await createPeerWithMic(callId, 'INBOUND');
        await pc.setRemoteDescription({ type: 'offer', sdp: sdpOffer });
        await pc.setLocalDescription(await pc.createAnswer());

        // Cap inbound answer ICE candidate gathering to max 3000ms
        const sdpAnswer = await localDescriptionWhenReady(pc, {
          maxWaitMs: 3000,
          minWaitMs: 200,
        });

        socketEmit('whatsapp:call:accept', { callId, sdpAnswer });
      } catch (error) {
        console.error('WhatsApp call accept failed:', error);
        toast.error('Could not answer the call. Check microphone permissions.');
        markCallEnded(callId, { status: 'FAILED' });
        socketEmit('whatsapp:call:reject', { callId });
      }
    },
    [createPeerWithMic, markCallEnded, socketEmit, upsertCall]
  );

  const rejectCall = useCallback(
    (callId: WhatsAppCallId) => {
      stopRingtone();
      socketEmit('whatsapp:call:reject', { callId });
      endedCallIdsRef.current.add(callId);
      teardownCallMedia(callId);
      dismissCall(callId);
    },
    [dismissCall, socketEmit, teardownCallMedia]
  );

  const hangUp = useCallback(
    (callId: WhatsAppCallId) => {
      stopRingtone();
      const session = callsRef.current[callId];
      const isTerminal =
        session?.status === 'ENDED' ||
        session?.status === 'FAILED' ||
        session?.status === 'REJECTED' ||
        session?.status === 'MISSED';

      if (isTerminal) {
        dismissCall(callId);
        setIsBusy(false);
        return;
      }

      const isOutboundPlaceholder = callId.startsWith('outbound-');
      if (!isOutboundPlaceholder) {
        socketEmit('whatsapp:call:terminate', { callId });
      }
      markCallEnded(callId, { status: 'ENDED' });
      if (isOutboundPlaceholder) {
        setIsBusy(false);
      }
    },
    [dismissCall, markCallEnded, socketEmit]
  );

  const toggleMute = useCallback((callId: WhatsAppCallId) => {
    const entry = peersRef.current.get(callId);
    if (!entry) return;

    const shouldMute = !mutedCallsRef.current.has(callId);
    entry.stream.getAudioTracks().forEach((track) => {
      const isHeldState = heldCallsRef.current.has(callId);
      track.enabled = !shouldMute && !isHeldState;
    });

    if (shouldMute) {
      mutedCallsRef.current.add(callId);
    } else {
      mutedCallsRef.current.delete(callId);
    }

    setCalls((prev) => ({ ...prev }));
  }, []);

  const isMuted = useCallback((callId: WhatsAppCallId) => mutedCallsRef.current.has(callId), []);

  const toggleHold = useCallback(
    (callId: WhatsAppCallId) => {
      const entry = peersRef.current.get(callId);
      if (!entry) return;

      const shouldHold = !heldCallsRef.current.has(callId);
      entry.stream.getAudioTracks().forEach((track) => {
        const isMutedState = mutedCallsRef.current.has(callId);
        track.enabled = !shouldHold && !isMutedState;
      });

      // Mute/unmute remote audio playback HTML element
      const el = document.getElementById(`wa-audio-${callId}`) as HTMLAudioElement | null;
      if (el) {
        el.muted = shouldHold;
      }

      if (shouldHold) {
        heldCallsRef.current.add(callId);
        upsertCall(callId, { status: 'CONNECTING' }); // Display holding state visually
      } else {
        heldCallsRef.current.delete(callId);
        upsertCall(callId, { status: 'ACTIVE' });
      }

      setCalls((prev) => ({ ...prev }));
    },
    [upsertCall]
  );

  const isHeld = useCallback((callId: WhatsAppCallId) => heldCallsRef.current.has(callId), []);

  const startOutboundCall = useCallback(
    async (
      conversationId: string,
      contact?: { firstName?: string; lastName?: string; phone?: string }
    ) => {
      if (isBusy) {
        toast.error(t('WHATSAPP_CALL_IN_PROGRESS'));
        return;
      }

      const tempId = `outbound-${Date.now()}`;
      const displayName = contactDisplayName(contact);
      setIsBusy(true);

      let resolvedDetails: { contactName: string; avatarUrl: string } | null = null;
      try {
        resolvedDetails = await resolveContactDetails(contact?.phone);
      } catch (err) {
        console.warn('[WA Call] Error resolving contact details before call:', err);
      }

      upsertCall(tempId, {
        callId: tempId,
        conversationId,
        direction: 'OUTBOUND',
        status: 'DIALING',
        contactName: resolvedDetails?.contactName || displayName,
        avatarUrl: resolvedDetails?.avatarUrl,
      });

      try {
        const { pc, stream } = await createPeerWithMic(tempId, 'OUTBOUND');
        await pc.setLocalDescription(await pc.createOffer());
        const sdpOffer = await localDescriptionWhenReady(pc, {
          maxWaitMs: OUTBOUND_ICE_MAX_MS,
          minWaitMs: OUTBOUND_ICE_MIN_MS,
        });

        // Keep the PC offer identical to what we send Meta (avoids answer/state mismatch after early ICE).
        if (pc.localDescription?.sdp !== sdpOffer) {
          await pc.setLocalDescription({ type: 'offer', sdp: sdpOffer });
        }

        const result = await initiateCall({ conversationId, sdpOffer }).unwrap();
        const callId = result.callId;

        if (!callId) {
          throw new Error('No call id returned');
        }

        peersRef.current.delete(tempId);
        peersRef.current.set(callId, { pc, stream });
        attachRemoteAudio(pc, callId);
        document.getElementById(`wa-audio-${tempId}`)?.remove();

        setCalls((prev) => {
          const placeholder = prev[tempId];
          const next = { ...prev };
          delete next[tempId];
          
          const isCallActive = placeholder?.status === 'ACTIVE';
          next[callId] = {
            ...placeholder,
            callId,
            conversationId,
            direction: 'OUTBOUND',
            status: isCallActive ? 'ACTIVE' : 'CONNECTING',
            connectedAt: placeholder?.connectedAt,
            contactName: placeholder?.contactName || displayName,
            avatarUrl: placeholder?.avatarUrl,
          };
          return next;
        });

        flushPendingAnswer(callId);
        startAnswerWatcher(callId, pc);
      } catch (error: any) {
        console.error('WhatsApp outbound call failed:', error);
        teardownPeer(tempId, peersRef.current);
        dismissCall(tempId);

        toast.error(
          getWhatsAppApiErrorMessage(error, t, 'WHATSAPP_CALL_INITIATE_FAILED', currentLang?.value)
        );
      } finally {
        setIsBusy(false);
      }
    },
    [
      createPeerWithMic,
      dismissCall,
      flushPendingAnswer,
      initiateCall,
      isBusy,
      startAnswerWatcher,
      t,
      currentLang?.value,
      upsertCall,
      resolveContactDetails,
    ]
  );

  useEffect(() => {
    if (status !== 'connected') return undefined;

    const handleIncoming = (payload: WhatsAppCallIncomingPayload) => {
      const { callId, conversationId, contactId, from, sdpOffer } = payload;
      if (!callId || endedCallIdsRef.current.has(callId)) return;

      pendingOffersRef.current.set(callId, sdpOffer);

      setCalls((prev) => {
        const alreadyPastRinging = prev[callId]?.status && prev[callId].status !== 'RINGING';
        if (alreadyPastRinging) {
          return prev;
        }

        if (!prev[callId]) {
          queueMicrotask(() => playRingtone());
        }

        return {
          ...prev,
          [callId]: {
            callId,
            conversationId,
            contactId,
            from,
            direction: 'INBOUND',
            status: 'RINGING',
            contactName: from,
          },
        };
      });

      // Dynamically resolve contact details and profile picture (avatarUrl)
      resolveContactDetails(from, contactId).then((details) => {
        if (details && !endedCallIdsRef.current.has(callId)) {
          upsertCall(callId, {
            contactName: details.contactName || from,
            avatarUrl: details.avatarUrl,
          });
        }
      });
    };

    const handleAnswer = (payload: WhatsAppCallAnswerPayload) => {
      const { callId, sdpAnswer } = payload;
      if (!callId || !sdpAnswer || endedCallIdsRef.current.has(callId)) return;
      void tryApplyRemoteAnswer(callId, sdpAnswer);
    };

    const handleConnected = (payload: WhatsAppCallConnectedPayload) => {
      const { callId } = payload;
      if (!callId || endedCallIdsRef.current.has(callId)) return;

      stopRingtone();
      upsertCall(callId, {
        status: 'ACTIVE',
        connectedAt: callsRef.current[callId]?.connectedAt ?? Date.now(),
      });
    };

    const handleEnded = (payload: WhatsAppCallEndedPayload) => {
      const { callId, reason, durationSec } = payload;
      if (!callId) return;

      stopRingtone();
      markCallEnded(callId, { status: 'ENDED', endReason: reason, durationSec });
    };

    const handleFailed = (payload: WhatsAppCallFailedPayload) => {
      const { callId, error } = payload;
      if (!callId) return;

      stopRingtone();
      if (error) {
        toast.error(error);
      }
      markCallEnded(callId, { status: 'FAILED', error });
    };

    const unsubIncoming = subscribe('whatsapp:call:incoming', handleIncoming);
    const unsubAnswer = subscribe('whatsapp:call:answer', handleAnswer);
    const unsubConnected = subscribe('whatsapp:call:connected', handleConnected);
    const unsubEnded = subscribe('whatsapp:call:ended', handleEnded);
    const unsubFailed = subscribe('whatsapp:call:failed', handleFailed);

    return () => {
      unsubIncoming();
      unsubAnswer();
      unsubConnected();
      unsubEnded();
      unsubFailed();
    };
  }, [markCallEnded, status, subscribe, tryApplyRemoteAnswer, upsertCall, resolveContactDetails]);

  // Stop ringtone whenever no inbound call is still ringing (safety net)
  useEffect(() => {
    const hasInboundRinging = Object.values(calls).some(
      (c) => c.status === 'RINGING' && c.direction === 'INBOUND'
    );
    if (!hasInboundRinging) {
      stopRingtone();
    }
  }, [calls]);

  // Play ringback while an outbound call is waiting to be answered
  useEffect(() => {
    const hasOutboundWaiting = Object.values(calls).some(
      (c) =>
        c.direction === 'OUTBOUND' &&
        !c.connectedAt &&
        (c.status === 'DIALING' || c.status === 'CONNECTING')
    );
    if (hasOutboundWaiting) {
      startRingback();
    } else {
      stopRingback();
    }
  }, [calls, startRingback, stopRingback]);

  const primaryCall = useMemo(() => {
    const list = Object.values(calls);
    return (
      list.find((c) => c.status === 'ACTIVE') ??
      list.find((c) => c.status === 'CONNECTING') ??
      list.find((c) => c.status === 'DIALING') ??
      list.find((c) => c.status === 'RINGING' && c.direction === 'INBOUND') ??
      list.find((c) => c.status === 'ENDED') ??
      list.find((c) => c.status === 'FAILED') ??
      null
    );
  }, [calls]);

  const value = useMemo(
    () => ({
      calls,
      primaryCall,
      isBusy,
      startOutboundCall,
      acceptCall,
      rejectCall,
      hangUp,
      toggleMute,
      isMuted,
      toggleHold,
      isHeld,
      selectedMicrophoneId,
      setSelectedMicrophoneId,
      microphones,
    }),
    [
      acceptCall,
      calls,
      hangUp,
      isBusy,
      isMuted,
      toggleHold,
      isHeld,
      primaryCall,
      rejectCall,
      startOutboundCall,
      toggleMute,
      selectedMicrophoneId,
      setSelectedMicrophoneId,
      microphones,
    ]
  );

  return <WhatsAppCallContext.Provider value={value}>{children}</WhatsAppCallContext.Provider>;
}
